import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import {
  InProductTrialsOverlayWindowGroupId,
  InProductTrialsOverlayWindowType,
  InProductTrialsOverlayWindowTypesToTrialType,
} from '../../types/InProductTrialsOverlayWindowType';
import { InProductTrialsOverlayContentWebComponent } from '../../inProductTrialsOverlayContent';
import { GzOverlay } from '../../../../../ui/gzInfoOverlay';
import { InProductTrialsAdditionalUserSettings } from '../types/InProductTrialsAdditionalUserSettings';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import {
  InProductTrialsOverlayWindowEventType,
  OnInProductTrialsOverlayWindowButtonClick,
} from '../../inProductTrialsOverlayContent/events/InProductTrialsOverlayWindowEventType';
import { LicenseChangePopup } from '../licenseChangePopup/LicenseChangePopup';
import { InProductTrialsOverlayWindowDetails } from '../types/InProductTrialsOverlayWindowDetails';
import { WEB_COMPONENT_IN_PRODUCT_TRIALS_OVERLAY_WINDOW } from 'apps/webcomponents/src/webcomponents.constants';
import { getInProductTrialsOverlayWindowConfigByWindowType } from './configs/in-product-trials-overlay-window-config';
import { MenuState } from '../types/MenuState';
import { GzToastNotificationWebComponent } from '../../../../../ui/gzToastNotification';
import { ToastActionButtonMode, ToastSeverity } from '../../../../../ui/gzToastNotification/type';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { InProductTrialsOverlayWindowLinks } from '../types/InProductTrialsOverlayWindowLinks';
import { trackDismissActionIfWelcomeBanner } from '../../../tracking/in-product-trials-tracking';
import { GzHeaderWebComponent } from '../../../../../ui/gzHeader';
import { buildHash, NavigationHelper } from '@nirvana/shared/navigation';

export class InProductTrialsOverlayWindowsAppView extends BaseWebComponentView {
  public overlayWindowNewSection: InProductTrialsOverlayWindowDetails | null;
  public overlayWindowsReadyToTry: Array<InProductTrialsOverlayWindowDetails> = [];
  public overlayWindowsTrialEnded: Array<InProductTrialsOverlayWindowDetails> = [];

  private menuState: MenuState;

  buildSuccessView(additionalUserSettings: InProductTrialsAdditionalUserSettings) {
    // set overlay window new section
    if (!additionalUserSettings.dismissedInProductTrialsNewSectionPopup) {
      this.overlayWindowNewSection = {
        type: InProductTrialsOverlayWindowType.ProductTrialsHubNewSection,
        groupId: InProductTrialsOverlayWindowGroupId.NewSection,
      };
      return;
    }

    // set overlay windows ready to try
    this.overlayWindowsReadyToTry = InProductTrialsAdditionalUserSettings.getAvailableInProductTrialsPopupsForGroupId(
      additionalUserSettings.shownInProductTrialsReadyToTryEndedPopups,
      InProductTrialsOverlayWindowGroupId.ReadyToTry
    );

    // set overlay windows trial ended
    this.overlayWindowsTrialEnded = InProductTrialsAdditionalUserSettings.getAvailableInProductTrialsPopupsForGroupId(
      additionalUserSettings.shownInProductTrialsReadyToTryEndedPopups,
      InProductTrialsOverlayWindowGroupId.TrialEnded
    );
  }

  public setMenuState(menuState: MenuState) {
    if (this.menuState === menuState) {
      return;
    }

    this.menuState = menuState;

    // update targets
    this.updateInProductTrialsOverlayWindowReadyToTryIfAvailable();
  }

  public onMenuScrollEvent() {
    // update targets
    this.updateInProductTrialsOverlayWindowReadyToTryIfAvailable(true);
  }

  public createInProductTrialsOverlayWindows() {
    this.createInProductTrialsOverlayWindowNewSectionIfAvailable();
    this.createInProductTrialsOverlayWindowReadyToTryIfAvailable();
    this.createInProductTrialsOverlayWindowTrialEndedIfAvailable();
  }

  public hideInProductTrialsOverlayWindowNewSectionIfAvailable(): boolean {
    if (!this.overlayWindowNewSection) {
      return false;
    }

    this.overlayWindowNewSection.component?.close();
    this.overlayWindowNewSection = null;

    return true;
  }

  private createInProductTrialsOverlayWindowNewSectionIfAvailable() {
    if (!this.overlayWindowNewSection) {
      return;
    }

    this.createInProductTrialsOverlayWindow(this.overlayWindowNewSection);
  }

  private createInProductTrialsOverlayWindowReadyToTryIfAvailable() {
    if (isEmpty(this.overlayWindowsReadyToTry)) {
      return;
    }

    this.createInProductTrialsOverlayWindow(this.overlayWindowsReadyToTry[0]);
  }

  private updateInProductTrialsOverlayWindowReadyToTryIfAvailable(shouldHideWhenOffscreen: boolean = false) {
    if (isEmpty(this.overlayWindowsReadyToTry)) {
      return;
    }

    this.updateInProductTrialsOverlayWindowPosition(this.overlayWindowsReadyToTry[0], null, shouldHideWhenOffscreen);
  }

  private createInProductTrialsOverlayWindowTrialEndedIfAvailable() {
    if (isEmpty(this.overlayWindowsTrialEnded)) {
      return;
    }

    this.createInProductTrialsOverlayWindow(this.overlayWindowsTrialEnded[0]);
  }

  private getTargetElementByConfig(overlayWindow: InProductTrialsOverlayWindowDetails) {
    const config = getInProductTrialsOverlayWindowConfigByWindowType(overlayWindow.type, this.menuState);
    const header = document.querySelector('gz-header') as GzHeaderWebComponent;
    return config.target === 'inProductTrials' ?
      header?.getMenuItemElement('inProductTrials') :
      Ext.getCmp(config.target)?.getEl().dom;
  }

  private async createInProductTrialsOverlayWindow(overlayWindow: InProductTrialsOverlayWindowDetails) {
    const config = getInProductTrialsOverlayWindowConfigByWindowType(overlayWindow.type, this.menuState);

    const targetElement = this.getTargetElementByConfig(overlayWindow);

    if (!targetElement) {
      return;
    }

    const gzInProductTrialsOverlayWindow = document.createElement(
      WEB_COMPONENT_IN_PRODUCT_TRIALS_OVERLAY_WINDOW
    ) as InProductTrialsOverlayContentWebComponent;

    gzInProductTrialsOverlayWindow.setAttribute('inProductTrialsOverlayWindowType', overlayWindow.type);
    gzInProductTrialsOverlayWindow.injectDependencies(this.dependencies);

    // build overlay component
    overlayWindow.component = GzOverlay.show(
      {
        content: gzInProductTrialsOverlayWindow,
        position: config.position,
      },
      targetElement
    );

    await gzInProductTrialsOverlayWindow.start();

    overlayWindow.component?.addEventListener(
      InProductTrialsOverlayWindowEventType.Default,
      (event: OnInProductTrialsOverlayWindowButtonClick) => {
        this.onInProductTrialsOverlayWindowButtonClick(overlayWindow, event.detail.eventType);
      }
    );

    targetElement.addEventListener('move', () => {
      this.updateInProductTrialsOverlayWindowPosition(overlayWindow, targetElement);
    });
  }

  private updateInProductTrialsOverlayWindowPosition(
    overlayWindow: InProductTrialsOverlayWindowDetails,
    targetElement?,
    shouldHideWhenOffscreen: boolean = false
  ) {
    if (!targetElement) {
      targetElement = this.getTargetElementByConfig(overlayWindow);
    }

    if (!targetElement) {
      return;
    }

    if (shouldHideWhenOffscreen && targetElement.getBoundingClientRect().top < 0) {
      // is offscreen
      overlayWindow.component?.close(false);
      return;
    }

    if (!overlayWindow.component?.isOpen) {
      overlayWindow.component?.open();
    }

    GzOverlay.updatePosition(overlayWindow.component, targetElement);
  }

  private onInProductTrialsOverlayWindowButtonClick(
    overlayWindow: InProductTrialsOverlayWindowDetails,
    eventType: InProductTrialsOverlayWindowEventType
  ) {
    switch (eventType) {
      case InProductTrialsOverlayWindowEventType.LearnMore:
        this.learnMoreInProductTrialsOverlayWindow(overlayWindow);
        break;
      case InProductTrialsOverlayWindowEventType.Dismiss:
        // track action: Dismiss in product trials welcome banner
        trackDismissActionIfWelcomeBanner(overlayWindow.type);
        this.dismissInProductTrialsOverlayWindow(overlayWindow);
        break;
      case InProductTrialsOverlayWindowEventType.AddModules:
        this.addRemoveModulesInProductTrialsOverlayWindow(overlayWindow, true);
        break;
      case InProductTrialsOverlayWindowEventType.RemoveModules:
        this.addRemoveModulesInProductTrialsOverlayWindow(overlayWindow, false);
        break;
    }
  }

  private addRemoveModulesInProductTrialsOverlayWindow(
    overlayWindow: InProductTrialsOverlayWindowDetails,
    isAddModulesAction: boolean
  ) {
    const confirmCallback = async () => {
      const confirmBtn = dialog?.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
      if (confirmBtn) {
        confirmBtn.setLoadingState(true);
      }

      try {
        await this.dependencies.api.clients.InProductTrials.ReconfigureAfterLicenseChangeAddRemoveModules(
          InProductTrialsOverlayWindowTypesToTrialType[overlayWindow.type],
          isAddModulesAction
        ).toPromise();

        // show success toast
        this.showSuccessToast();
      } catch (exception) {
        // show error toast
        this.showErrorToast(exception);
      }

      dialog?.close();
      this.dismissInProductTrialsOverlayWindow(overlayWindow);
    };

    const cancelCallback = () => {
      this.dismissInProductTrialsOverlayWindow(overlayWindow);
    };

    const dialog = LicenseChangePopup.showLicenseChangePopup(
      this.dependencies.translateFn,
      overlayWindow.type,
      false,
      cancelCallback,
      confirmCallback
    );
  }

  private learnMoreInProductTrialsOverlayWindow(overlayWindow: InProductTrialsOverlayWindowDetails) {
    switch (overlayWindow.groupId) {
      case InProductTrialsOverlayWindowGroupId.NewSection:
        overlayWindow.component?.close();
        this.overlayWindowNewSection = null;
        this.dismissInProductTrialsOverlayWindow(overlayWindow);
        // show in product trial app component from banner learn more button
        NavigationHelper.navigateToProductsHub(undefined, 'button|b|banner|trial-offer');
        break;

      case InProductTrialsOverlayWindowGroupId.ReadyToTry:
      case InProductTrialsOverlayWindowGroupId.TrialEnded:
        window.open(InProductTrialsOverlayWindowLinks[overlayWindow.type], '_blank');
        break;
    }
  }

  private dismissInProductTrialsOverlayWindow(overlayWindow: InProductTrialsOverlayWindowDetails) {
    switch (overlayWindow.groupId) {
      case InProductTrialsOverlayWindowGroupId.NewSection:
        overlayWindow.component?.close();
        this.overlayWindowNewSection = null;
        this.dependencies.api.clients.InProductTrials.DismissInProductTrialsOverlayWindowNewSection().toPromise();
        break;

      case InProductTrialsOverlayWindowGroupId.ReadyToTry:
        overlayWindow.component?.close();
        this.overlayWindowsReadyToTry = this.overlayWindowsReadyToTry.filter(
          window => window.type !== overlayWindow.type
        );
        this.dependencies.api.clients.InProductTrials.DismissInProductTrialsOverlayWindowReadyToTryTrialEnded(
          overlayWindow.type
        ).toPromise();
        this.createInProductTrialsOverlayWindowReadyToTryIfAvailable();
        break;

      case InProductTrialsOverlayWindowGroupId.TrialEnded:
        overlayWindow.component?.close();
        this.overlayWindowsTrialEnded = this.overlayWindowsTrialEnded.filter(
          window => window.type !== overlayWindow.type
        );
        this.dependencies.api.clients.InProductTrials.DismissInProductTrialsOverlayWindowReadyToTryTrialEnded(
          overlayWindow.type
        ).toPromise();
        this.createInProductTrialsOverlayWindowTrialEndedIfAvailable();
        break;
    }
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Task created.'),
      severity: ToastSeverity.SUCCESS,
      actionButtonText: this.dependencies.translateFn('View status.'),
      hasAction: true,
      actionCallback: buildHash('/tasks'),
      actionButtonMode: ToastActionButtonMode.LINK,
    });
  }

  private showErrorToast(error: Error) {
    const errorMessage =
      error.message ||
      this.dependencies.translateFn(
        'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
      );

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }
}
